import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Bot, Loader2, RefreshCw, Phone, CheckCircle2, XCircle,
  MessageSquare, QrCode, Unplug, Clock, TrendingUp, AlertTriangle,
  Hash
} from "lucide-react";

export function SimoneWhatsAppTab({ adminAxios }) {
  const [status, setStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [stats, setStats] = useState(null);
  const [pairingCode, setPairingCode] = useState(null);

  const pollRef = useRef(null);
  const adminAxiosRef = useRef(adminAxios);
  adminAxiosRef.current = adminAxios;

  // ── status fetch ────────────────────────────────────────────────────────────
  const fetchStatus = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const [statusRes, statsRes] = await Promise.all([
        adminAxiosRef.current.get(`/simone/whatsapp/status`),
        adminAxiosRef.current.get(`/simone/stats`),
      ]);
      setStatus(statusRes.data);
      setQrCode(statusRes.data.qr || null);
      setStats(statsRes.data);
    } catch {
      setStatus({ status: "disconnected", phone: null });
    }
    if (showLoading) setLoading(false);
  }, []);

  // Background poll every 15s — stops when connected
  useEffect(() => {
    fetchStatus(true);
    const interval = setInterval(() => fetchStatus(false), 15000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // ── QR poll: fast poll (2.5s) after connect/refresh until QR appears ───────
  const startQRPoll = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const start = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - start > 90000) { clearInterval(pollRef.current); return; }
      try {
        const res = await adminAxiosRef.current.get(`/simone/whatsapp/status`);
        const s = res.data.status;
        setStatus(res.data);
        if (res.data.qr) setQrCode(res.data.qr);
        if (s === "waiting_qr" && res.data.qr) { clearInterval(pollRef.current); }
        if (s === "connected") {
          setQrCode(null);
          setPairingCode(null);
          clearInterval(pollRef.current);
          toast.success("Simone conectada!");
        }
        if (s === "qr_expired") { clearInterval(pollRef.current); }
      } catch {}
    }, 2500);
  }, []);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ── actions ─────────────────────────────────────────────────────────────────
  const handleConnect = async () => {
    setActing(true);
    setQrCode(null);
    try {
      const res = await adminAxiosRef.current.post(`/simone/whatsapp/connect`);
      setStatus(res.data);
      if (res.data.qr) setQrCode(res.data.qr);
      if (res.data.status === "connected") {
        toast.success("Simone já está conectada!");
      } else {
        toast.info("A gerar QR Code...");
        startQRPoll();
      }
    } catch {
      toast.error("Erro ao conectar WhatsApp");
    }
    setActing(false);
  };

  const handleRefreshQR = async () => {
    setActing(true);
    setQrCode(null);
    try {
      const res = await adminAxiosRef.current.post(`/simone/whatsapp/refresh-qr`);
      setStatus(res.data);
      if (res.data.qr) { setQrCode(res.data.qr); } else { startQRPoll(); }
      toast.info("A gerar novo QR Code...");
    } catch {
      toast.error("Erro ao gerar QR Code");
    }
    setActing(false);
  };

  const handleResetSession = async () => {
    if (!window.confirm("Limpar sessão antiga e gerar QR novo?")) return;
    setActing(true);
    setQrCode(null);
    try {
      await adminAxiosRef.current.post(`/simone/whatsapp/reset-session`);
      toast.info("Sessão limpa — a gerar QR...");
      await new Promise(r => setTimeout(r, 1500));
      const res = await adminAxiosRef.current.post(`/simone/whatsapp/connect`);
      setStatus(res.data);
      if (res.data.qr) { setQrCode(res.data.qr); } else { startQRPoll(); }
    } catch {
      toast.error("Erro ao resetar sessão");
    }
    setActing(false);
  };

  const handlePairCode = async () => {
    setActing(true);
    setQrCode(null);
    setPairingCode(null);
    try {
      const res = await adminAxiosRef.current.post(`/simone/whatsapp/pair`);
      if (res.data.code) {
        setPairingCode(res.data.code);
        setStatus(prev => ({ ...prev, status: "pairing" }));
        toast.info("Código gerado — introduza no WhatsApp em 60s");
        startQRPoll();
      } else {
        toast.error(res.data.error || "Erro ao gerar código");
      }
    } catch {
      toast.error("Erro ao gerar código de pareamento");
    }
    setActing(false);
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Desconectar o WhatsApp da Simone?")) return;
    setActing(true);
    try {
      await adminAxiosRef.current.post(`/simone/whatsapp/disconnect`);
      setStatus({ status: "disconnected", phone: null });
      setQrCode(null);
      toast.success("WhatsApp desconectado");
    } catch {
      toast.error("Erro ao desconectar");
    }
    setActing(false);
  };

  // ── status badge ─────────────────────────────────────────────────────────────
  const getStatusBadge = () => {
    const st = status?.status || "disconnected";
    const configs = {
      connected:    { color: "bg-green-100 text-green-700",  label: "Conectado",       icon: CheckCircle2 },
      waiting_qr:  { color: "bg-amber-100 text-amber-700",  label: "Aguardando QR",   icon: QrCode },
      pairing:     { color: "bg-purple-100 text-purple-700",label: "Aguardando Código",icon: Hash },
      connecting:  { color: "bg-blue-100 text-blue-700",    label: "Conectando...",   icon: Loader2 },
      reconnecting:{ color: "bg-blue-100 text-blue-700",    label: "Reconectando...", icon: RefreshCw },
      disconnected:{ color: "bg-red-100 text-red-700",      label: "Desconectado",    icon: XCircle },
      qr_expired:  { color: "bg-orange-100 text-orange-700",label: "QR Expirado",     icon: Clock },
      qr_required: { color: "bg-orange-100 text-orange-700",label: "QR Necessário",   icon: AlertTriangle },
    };
    const cfg = configs[st] || configs.disconnected;
    const Icon = cfg.icon;
    return (
      <Badge className={`${cfg.color} gap-1.5`}>
        <Icon className={`w-3.5 h-3.5 ${st === "connecting" || st === "reconnecting" ? "animate-spin" : ""}`} />
        {cfg.label}
      </Badge>
    );
  };

  const st = status?.status || "disconnected";
  const isConnected = st === "connected";
  const isStuck = st === "reconnecting" || st === "qr_expired" || st === "qr_required";
  const needsQR = !isConnected;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="simone-whatsapp-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">IA Simone - WhatsApp</h2>
            <p className="text-sm text-gray-500">Gerenciar conexão WhatsApp da assistente de vendas</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchStatus(true)} disabled={loading || acting}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_conversations || 0}</p>
                <p className="text-xs text-gray-500">Conversas Totais</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total_leads || 0}</p>
                <p className="text-xs text-gray-500">Leads Capturados</p>
              </div>
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.recent_sessions || 0}</p>
                <p className="text-xs text-gray-500">Sessões Recentes</p>
              </div>
            </div>
          </CardContent></Card>
        </div>
      )}

      {/* Connection Status */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Phone className="w-5 h-5 text-gray-500" />
              Status da Conexão
            </CardTitle>
            {getStatusBadge()}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">

          <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Número WhatsApp da Simone</p>
              <p className="text-lg font-bold text-gray-900">+34 611 564 839</p>
            </div>
            {status?.phone && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Conectado como</p>
                <p className="text-sm font-semibold text-green-600">+{status.phone}</p>
              </div>
            )}
          </div>

          {/* Pairing Code Section */}
          {pairingCode && !isConnected && (
            <div className="flex flex-col items-center p-6 border-2 border-solid rounded-xl bg-purple-50 border-purple-300">
              <Hash className="w-8 h-8 text-purple-500 mb-3" />
              <p className="text-sm font-semibold text-purple-800 mb-1">Código de Pareamento</p>
              <p className="text-xs text-purple-600 mb-4 text-center">
                No WhatsApp do <strong>+34 611 564 839</strong>:<br />
                Definições → Aparelhos Vinculados → Vincular Aparelho → Vincular com número de telefone
              </p>
              <div className="bg-white border-2 border-purple-300 rounded-xl px-8 py-4 mb-3">
                <p className="text-4xl font-mono font-bold tracking-[0.3em] text-purple-800 select-all">
                  {pairingCode}
                </p>
              </div>
              <p className="text-xs text-purple-500">O código é válido por ~60 segundos</p>
            </div>
          )}

          {/* QR Code Section */}
          {needsQR && !pairingCode && (
            <div className="flex flex-col items-center p-6 border-2 border-dashed rounded-xl
              bg-amber-50 border-amber-200">
              <p className="text-sm font-medium text-amber-800 mb-4">
                Escaneie com o WhatsApp do número +34 611 564 839
              </p>

              {acting && !qrCode ? (
                <div className="w-64 h-64 flex flex-col items-center justify-center gap-3">
                  <Loader2 className="w-10 h-10 text-amber-500 animate-spin" />
                  <p className="text-sm text-amber-600">A gerar QR Code...</p>
                </div>
              ) : qrCode ? (
                <>
                  <img src={qrCode} alt="QR Code" className="w-64 h-64 rounded-lg shadow-lg" />
                  {st === "qr_expired" && (
                    <p className="text-xs text-red-500 mt-2 font-semibold">
                      ⚠️ Este QR já expirou — clique em "Novo QR" abaixo
                    </p>
                  )}
                  {st === "waiting_qr" && (
                    <p className="text-xs text-amber-600 mt-2">
                      Aponte a câmera do telemóvel para este QR Code
                    </p>
                  )}
                </>
              ) : (
                <div className="w-64 h-64 flex flex-col items-center justify-center gap-3">
                  {isStuck ? (
                    <>
                      <AlertTriangle className="w-10 h-10 text-amber-500" />
                      <p className="text-sm text-amber-700 font-semibold text-center">
                        Sessão bloqueada
                      </p>
                      <p className="text-xs text-amber-600 text-center">
                        Clique em "Resetar Sessão" para limpar e gerar QR novo
                      </p>
                    </>
                  ) : (
                    <>
                      <QrCode className="w-10 h-10 text-amber-300" />
                      <p className="text-sm text-amber-600">Clique em "Conectar" para gerar QR</p>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {isConnected ? (
              <Button variant="destructive" onClick={handleDisconnect} disabled={acting}>
                {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unplug className="w-4 h-4 mr-2" />}
                Desconectar
              </Button>
            ) : (
              <>
                <Button
                  onClick={handleConnect}
                  disabled={acting}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="simone-connect-btn"
                >
                  {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <QrCode className="w-4 h-4 mr-2" />}
                  Conectar (QR)
                </Button>

                <Button
                  onClick={handlePairCode}
                  disabled={acting}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="simone-pair-btn"
                >
                  {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Hash className="w-4 h-4 mr-2" />}
                  Parear por Código
                </Button>

                <Button variant="outline" onClick={handleRefreshQR} disabled={acting} data-testid="simone-refresh-qr-btn">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Novo QR
                </Button>

                <Button
                  variant="outline"
                  onClick={handleResetSession}
                  disabled={acting}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="simone-reset-session-btn"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Resetar Sessão
                </Button>
              </>
            )}
          </div>

          {(st === "qr_expired" || isStuck) && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              💡 <strong>QR expirado ou sessão bloqueada?</strong> Tente <strong>Parear por Código</strong> (mais fiável) ou clique em <strong>Resetar Sessão</strong> e depois <strong>Conectar (QR)</strong>.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <Bot className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="font-bold text-amber-900 mb-1">Sobre a IA Simone</h3>
              <p className="text-sm text-amber-800 leading-relaxed">
                A Simone é a assistente de vendas e suporte exclusiva do Kaiso System.
                Ela responde dúvidas sobre a plataforma, preços, funcionalidades e ajuda
                a converter visitantes em clientes. Diferente da Karol (IA dos restaurantes),
                a Simone atua apenas no site principal e no WhatsApp da plataforma.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default SimoneWhatsAppTab;
